/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/

import React, { useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

type BorderedFileUploadProps = {
  onChange?: (files: File[] | null) => void;
  appliedColor?: string;
  customIcon?: string;
  multipleFiles?: boolean;
};

const BorderedFileUpload = ({
  onChange,
  appliedColor,
  customIcon,
  multipleFiles = false,
}: BorderedFileUploadProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const theme = useTheme();
  const borderColor = appliedColor || theme.palette.primary.main;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = e.target.files ? Array.from(e.target.files) : [];

    const updatedFiles = multipleFiles
      ? [...files, ...uploadedFiles]
      : uploadedFiles.slice(0, 1);

    setFiles(updatedFiles);
    onChange?.(updatedFiles.length ? updatedFiles : null);
  };

  const handleUploadClick = () => {
    document.getElementById("file-input")?.click();
  };

  const handleRemoveFile = (fileNameToRemove: string) => {
    const updatedFiles = files.filter((file) => file.name !== fileNameToRemove);
    setFiles(updatedFiles);
    onChange?.(updatedFiles.length ? updatedFiles : null);
  };

  const handleViewFile = (file: File) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, "_blank");
  };

  const UploadBox = () => (
    <Box
      sx={{
        border: `3px dashed ${borderColor}`,
        borderRadius: "12px",
        padding: "20px",
        textAlign: "center",
        background: "#FFFFFF",
        cursor: "pointer",
        width: "100%",
      }}
      onClick={handleUploadClick}
    >
      <input
        type="file"
        id="file-input"
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="*/*"
        multiple={multipleFiles}
      />
      {customIcon ? (
        <img src={customIcon} alt="upload" style={{ width: 50, height: 50 }} />
      ) : (
        <CloudUploadIcon sx={{ fontSize: 50, color: borderColor }} />
      )}
      <Typography variant="body2" color="textSecondary" mt={1}>
        Drag & drop to upload or click to browse files
      </Typography>
    </Box>
  );

  const FileItem = ({ file }: { file: File }) => (
    <Box
      key={file.name}
      sx={{
        backgroundColor: "#fff",
        p: 1.5,
        borderRadius: "8px",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: 1,
        width: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          color: borderColor,
          flexGrow: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
        title={file.name}
      >
        {file.name}
      </Typography>
      <Stack direction="row" spacing={1} flexShrink={0}>
        <Button
          variant="text"
          startIcon={<VisibilityIcon />}
          onClick={() => handleViewFile(file)}
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            color: borderColor,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          View
        </Button>
        <Button
          variant="text"
          startIcon={<DeleteIcon />}
          onClick={() => handleRemoveFile(file.name)}
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            color: borderColor,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Remove
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Stack width="100%" spacing={2}>
      {/* Upload UI + Files */}
      {multipleFiles ? (
        <>
          <UploadBox />
          {files.map((file, index) => (
            <FileItem key={index} file={file} />
          ))}
        </>
      ) : (
        <>{files.length === 0 ? <UploadBox /> : <FileItem file={files[0]} />}</>
      )}
    </Stack>
  );
};

export default BorderedFileUpload;
